import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import {
    Package, ChevronLeft, Search, Trash2,
    Eye, EyeOff, AlertCircle, X
} from "lucide-react";

const AdminProductManagement = () => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    const fetchProducts = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            let url = `${API_BASE_URL}/api/admin/products`;
            const params = new URLSearchParams();
            if (categoryFilter) params.append("categoryId", categoryFilter);
            if (statusFilter !== "") params.append("status", statusFilter);
            if (params.toString()) url += `?${params.toString()}`;

            const response = await fetch(url, {
                headers: { Authorization: token }
            });

            if (!response.ok) {
                if (response.status === 403) {
                    throw new Error("คุณไม่มีสิทธิ์เข้าถึงหน้านี้");
                }
                throw new Error("Failed to fetch products");
            }

            const data = await response.json();
            setProducts(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [categoryFilter, statusFilter]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleHideProduct = async (productId) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}/hide`, {
                method: "PUT",
                headers: { Authorization: token }
            });

            if (response.ok) {
                fetchProducts();
                setSelectedProduct(null);
                alert("ซ่อนสินค้าสำเร็จ");
            } else {
                alert("เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const handleShowProduct = async (productId) => {
        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}/show`, {
                method: "PUT",
                headers: { Authorization: token }
            });

            if (response.ok) {
                fetchProducts();
                setSelectedProduct(null);
                alert("แสดงสินค้าสำเร็จ");
            } else {
                alert("เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDeleteProduct = async (productId) => {
        if (!window.confirm("คุณแน่ใจหรือไม่ที่จะลบสินค้านี้? การลบจะไม่สามารถย้อนกลับได้")) return;

        setActionLoading(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/api/admin/products/${productId}`, {
                method: "DELETE",
                headers: { Authorization: token }
            });

            if (response.ok) {
                fetchProducts();
                setSelectedProduct(null);
                alert("ลบสินค้าสำเร็จ");
            } else {
                alert("เกิดข้อผิดพลาด");
            }
        } catch (err) {
            alert("เกิดข้อผิดพลาด");
        } finally {
            setActionLoading(false);
        }
    };

    const getCategoryName = (categoryId) => {
        const category = PRODUCT_CATEGORIES?.find(c => c.id === categoryId);
        return category?.name || `Category ${categoryId}`;
    };

    const getStatusBadge = (status) => {
        if (status === 1) {
            return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">แสดงอยู่</span>;
        } else {
            return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">ซ่อนอยู่</span>;
        }
    };

    const filteredProducts = products.filter(product =>
        product.productName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sellerName?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-100 flex items-center justify-center">
                <div className="text-center bg-white p-8 rounded-xl shadow-sm">
                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">{error}</p>
                    <Link to="/admin" className="text-red-600 hover:underline">กลับหน้า Admin</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100 py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="p-2 hover:bg-gray-200 rounded-lg transition-colors">
                            <ChevronLeft className="w-6 h-6" />
                        </Link>
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-green-500 rounded-lg">
                                <Package className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">จัดการสินค้า</h1>
                                <p className="text-sm text-gray-500">ทั้งหมด {products.length} รายการ</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl p-4 shadow-sm mb-6">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="ค้นหาชื่อสินค้าหรือผู้ขาย..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                            />
                        </div>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        >
                            <option value="">ทุกหมวดหมู่</option>
                            {PRODUCT_CATEGORIES?.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none"
                        >
                            <option value="">ทุกสถานะ</option>
                            <option value="1">แสดงอยู่</option>
                            <option value="0">ซ่อนอยู่</option>
                        </select>
                    </div>
                </div>

                {/* Products Table */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ชื่อสินค้า</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">หมวดหมู่</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ราคา</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ผู้ขาย</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">สถานะ</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredProducts.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                        ไม่พบสินค้า
                                    </td>
                                </tr>
                            ) : (
                                filteredProducts.map((product) => (
                                    <tr key={product.productId} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-500">#{product.productId}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900 max-w-xs truncate">
                                            {product.productName}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{getCategoryName(product.categoryId)}</td>
                                        <td className="px-6 py-4 text-sm text-red-600 font-medium">฿{product.price?.toLocaleString()}</td>
                                        <td className="px-6 py-4 text-sm text-gray-500">{product.sellerName}</td>
                                        <td className="px-6 py-4">{getStatusBadge(product.status)}</td>
                                        <td className="px-6 py-4 text-right">
                                            <button
                                                onClick={() => setSelectedProduct(product)}
                                                className="text-red-600 hover:text-red-700 font-medium text-sm"
                                            >
                                                จัดการ
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Product Detail Modal */}
                {selectedProduct && (
                    <div className="fixed inset-0 z-50 overflow-y-auto">
                        <div className="flex items-center justify-center min-h-screen px-4">
                            <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedProduct(null)} />
                            <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6 z-10">
                                <div className="flex justify-between items-center mb-6">
                                    <h3 className="text-xl font-bold text-gray-900">จัดการสินค้า</h3>
                                    <button onClick={() => setSelectedProduct(null)} className="p-2 hover:bg-gray-100 rounded-lg">
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="space-y-4 mb-6">
                                    <div>
                                        <p className="text-sm text-gray-500">ชื่อสินค้า</p>
                                        <p className="font-medium">{selectedProduct.productName}</p>
                                    </div>
                                    <div className="flex gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">ราคา</p>
                                            <p className="font-medium text-red-600">฿{selectedProduct.price?.toLocaleString()}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">จำนวน</p>
                                            <p className="font-medium">{selectedProduct.quantity}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">ผู้ขาย</p>
                                        <p className="font-medium">{selectedProduct.sellerName}</p>
                                    </div>
                                    <div>
                                        <p className="text-sm text-gray-500">สถานะ</p>
                                        {getStatusBadge(selectedProduct.status)}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <Link
                                        to={`/product-details/${selectedProduct.productId}`}
                                        className="w-full py-3 px-4 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Eye size={18} />
                                        ดูสินค้า
                                    </Link>

                                    {selectedProduct.status === 1 ? (
                                        <button
                                            onClick={() => handleHideProduct(selectedProduct.productId)}
                                            disabled={actionLoading}
                                            className="w-full py-3 px-4 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <EyeOff size={18} />
                                            ซ่อนสินค้า
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleShowProduct(selectedProduct.productId)}
                                            disabled={actionLoading}
                                            className="w-full py-3 px-4 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <Eye size={18} />
                                            แสดงสินค้า
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleDeleteProduct(selectedProduct.productId)}
                                        disabled={actionLoading}
                                        className="w-full py-3 px-4 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <Trash2 size={18} />
                                        ลบสินค้า
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminProductManagement;
